import {
  Box,
  Button,
  Card,
  CardBody, CardFooter, CardHeader, Center, Divider, Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Image,
  Input, Popover, PopoverBody, PopoverContent, PopoverHeader, PopoverTrigger, Spacer, Stack, Tag,
  Text, useToast
} from "@chakra-ui/react";
import { createRef, useEffect, useState } from "react";
import { set, SubmitHandler, useForm } from "react-hook-form";
import { FaChevronLeft, FaChevronRight, FaList, FaListUl, FaTable } from "react-icons/fa6";
import { TbFilterEdit } from "react-icons/tb";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { patchIndicatorsValues, patchIndicatorValues } from "service/api/document.api";
import { updateSelectedDocumentIndicators, updateSelectedDocumentSubtarget } from "store/reducers/documents.slice";
import { useAppDispatch } from "store/store.hook";
import { SubTarget } from "types/SubTarget";
import { checkIfIndicatorIsInRolesArray, explodeIndicatorId, groupByIndicator } from "utils/utils";
import AttachmentsList from "../attachmentsList/AttachmentsList";
import SourcesList from "../sourcesList/SourcesList";

type SubTargetFormProps = {
  documentId: string;
  targetId: string;
  subTarget: SubTarget;
  isReadOnly?: boolean;
  timePeriod: string | null;
};

/**
 * SubTargetForm component props.
 * 
 * @typedef {Object} SubTargetFormProps
 * @property {string} documentId - The ID of the document.
 * @property {string} targetId - The ID of the target.
 * @property {SubTarget} subTarget - The sub-target object.
 * @property {boolean} [isReadOnly] - Flag indicating if the form is read-only.
 * @property {string | null} timePeriod - The time period for the form.
 */

/**
 * SubTargetForm component.
 * 
 * This component renders a form for editing sub-targets within a document. It allows users to view and edit indicators associated with actions under a sub-target. The form supports both grid and list views for indicators and includes functionality for saving changes.
 * 
 * @param {SubTargetFormProps} props - The props for the component.
 * @returns {JSX.Element} The rendered SubTargetForm component.
 * 
 * @component
 * @example
 * const documentId = "123";
 * const targetId = "456";
 * const subTarget = { ... };
 * const isReadOnly = false;
 * const timePeriod = "2023";
 * 
 * <SubTargetForm 
 *   documentId={documentId} 
 *   targetId={targetId} 
 *   subTarget={subTarget} 
 *   isReadOnly={isReadOnly} 
 *   timePeriod={timePeriod} 
 * />
 */

export default function SubTargetForm({
  documentId,
  targetId,
  subTarget,
  isReadOnly,
  timePeriod,
}: SubTargetFormProps) {


  const dispatch = useAppDispatch();
  const toast = useToast();
  const subTargetId = subTarget?._id;
  const target = useSelector((state: any) => state.documents.documents.find((doc: any) => doc._id === documentId)?.targets.find((target: any) => target._id === targetId));
  const divisions = useSelector((state: any) => state.divisions.divisions);
  const [isGridView, setisGridView] = useState(false);
  const [showOnlyEditable, setShowOnlyEditable] = useState<boolean>(false);
  const organizationalUnits = useSelector((state: any) => state.organizationalunits.organizationalUnits);
  const sources = useSelector((state: any) => state.sources.sources);

  const getNamesFromCodes = (codes: string[], data: any[]) => {
    let names: any = [];
    codes.map((code) => {
      const name = data.find((item: any) => item.code === code)?.name;
      if (name) {
        names.push(name);
      }
    });
    return names;
  }

  const retrieveDefaultValues =
    () => {
      let defaultValues: any = {};
      const subtarget = target?.subtargets.find((subtarget: any) => subtarget._id === subTargetId);
      subtarget?.actions.map((action: any) => {
        action?.indicators.map((indicator: any) => {
          divisions.map((departmemnt: any) => {

            let valuesToSet = '';
            if (indicator?.values && timePeriod in indicator?.values) {
              valuesToSet = indicator?.values[timePeriod]?.[departmemnt.code] ?? '';
            }

            defaultValues[`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${departmemnt._id}`] = valuesToSet;
          })
        })
      })
      return defaultValues;
    }

  const { register, handleSubmit, formState, reset, getValues } = useForm({
    defaultValues: retrieveDefaultValues(),
  });


  const [totals, setTotals] = useState<any>({});
  const [totalsDepartments, setTotalsDepartments] = useState<any>({});
  const [totalsCentralAdministration, setTotalsCentralAdministration] = useState<any>({});

  const roles = useSelector((state: any) => state.user.user?.roles);
  const isUserAdmin = useSelector((state: any) => state.user.user?.isAdmin);
  const attributesPnrr = useSelector((state: any) => state.attributes.attributesPnrr);
  const attributesSdg = useSelector((state: any) => state.attributes.attributesSdg);
  const attributesAnvur = useSelector((state: any) => state.attributes.attributesAnvur);

  useEffect(() => {
    reset(retrieveDefaultValues());
  }, [timePeriod])
  useEffect(() => {
    reset(retrieveDefaultValues());
  }, [subTarget])


  useEffect(() => {


    let defaultTotalsDepartments: any = {};

    subTarget?.actions.map((action) => {
      action.indicators.map((indicator) => {
        const sumArr = divisions.map((department: any) => {
          if (department.isDepartment) {
            const val = parseFloat(getValues(`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department._id}`));

            if (isNaN(val)) {
              return 0;
            } else {
              return val;
            }
          }
        });

        //sum all the values of the departments
        let sum = 0;
        for (let i = 0; i < sumArr.length; i++) {
          //sum only if is a number
          if (!isNaN(sumArr[i]))
            sum += sumArr[i];
        }

        defaultTotalsDepartments[`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`] = sum;
      })
    })



    let defaultTotalsCentralAdministration: any = {};
    subTarget?.actions.map((action) => {
      action.indicators.map((indicator) => {
        const sumArray = divisions.map((department: any) => {
          if (department.isCentralAdministration) {
            const val = parseFloat(getValues(`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department._id}`));
            if (isNaN(val)) {
              return 0;
            } else {
              return val;
            }
          }
        }
        );

        //sum all the values of the central administration
        let sum = 0;
        for (let i = 0; i < sumArray.length; i++) {
          //sum only if is a number
          if (!isNaN(sumArray[i]))
            sum += sumArray[i];
        }

        defaultTotalsCentralAdministration[`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`] = sum;
      })
    })
    setTotalsCentralAdministration(defaultTotalsCentralAdministration);
    setTotalsDepartments(defaultTotalsDepartments);
  }, [subTarget, divisions, timePeriod]);



  const onSubmit: SubmitHandler<any> = (data) => {
    const indicatorsNewValues = groupByIndicator(data, divisions)

    patchIndicatorsValues(documentId, targetId, subTargetId, timePeriod, indicatorsNewValues).then((res: any) => {

      dispatch(updateSelectedDocumentSubtarget({ newSubtarget: res, subTargetId: subTargetId }));
      toast({
        title: "Modifiche salvate",
        status: "success",
        duration: 9000,
        isClosable: true,
        position: "top",
      });

    }).catch((err: any) => {

      toast({
        title: "Errore",
        description: "Errore durante il salvataggio delle modifiche",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });
    });
  }

  const saveData = (id: string) => {

    let newValues: any = {};
    divisions.map((division: any) => {
      const indicatorId = id + '-' + division._id;
      newValues[indicatorId] = getValues(indicatorId);
    })

    const { targetId, subTargetId, actionId, indicatorId, } = explodeIndicatorId(id);

    let newValuesToPatch: any = {};
    divisions.map((division: any) => {
      const indicatorId = id + '-' + division._id;
      newValuesToPatch[division.code] = getValues(indicatorId);
    })

    patchIndicatorValues(
      documentId,
      targetId,
      subTargetId,
      actionId,
      indicatorId,
      timePeriod,
      newValuesToPatch
    ).then((res) => {
      dispatch(updateSelectedDocumentIndicators({ indicators: newValues, timePeriod: timePeriod, divisions: divisions }));
      toast({
        title: "Modifiche salvate",
        status: "success",
        duration: 9000,
        isClosable: true,
        position: "top",
      });

    }).catch((err) => {
      toast({
        title: "Errore",
        description: "Errore durante il salvataggio delle modifiche",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });
    });
  }

  let counter = -1;
  const allIndicators = subTarget?.actions.map((action) => action.indicators).flat();
  //create a ref for each indicator, index is the indicator index
  const elementRefs = allIndicators?.reduce((acc: any, indicator, index) => {
    acc[indicator.index] = createRef();
    return acc;
  }, {});


  const scrollLeft = (index: string) => {

    const element = elementRefs[index]?.current;
    const currentScroll = element?.scrollLeft;

    if (element) {
      element.scrollTo({ left: currentScroll - 300, behavior: 'smooth' });
    }
  };

  const scrollRight = (index: string) => {

    const element = elementRefs[index]?.current;
    const currentScroll = element?.scrollLeft;
    if (element) {
      element.scrollTo({ left: currentScroll + 300, behavior: 'smooth' });
    }

  };

  const updateTotals = (id: string) => {
    const { targetId, subTargetId, actionId, indicatorId } = explodeIndicatorId(id);

    const sumCentralAdministration = divisions.reduce((acc: number, department: { isCentralAdministration: any; _id: any; code: any }) => {
      if (department.isCentralAdministration) {

        let value = parseFloat(getValues(`indicator-${targetId}-${subTargetId}-${actionId}-${indicatorId}-${department._id}`));

        if (isNaN(value)) {
          value = 0;
        }

        return acc + value;
      }
      return acc;

    }, 0);

    const sumDepartments = divisions.reduce((acc: number, department: { isDepartment: any; _id: any; code: any }) => {
      if (department.isDepartment) {

        let value = parseFloat(getValues(`indicator-${targetId}-${subTargetId}-${actionId}-${indicatorId}-${department._id}`));

        if (isNaN(value)) {
          value = 0;
        }

        return acc + value;
      }
      return acc;
    }, 0);

    setTotalsCentralAdministration((prevTotals: any) => ({
      ...prevTotals,
      [`indicator-${targetId}-${subTargetId}-${actionId}-${indicatorId}`]: sumCentralAdministration
    }));

    setTotalsDepartments((prevTotals: any) => ({
      ...prevTotals,
      [`indicator-${targetId}-${subTargetId}-${actionId}-${indicatorId}`]: sumDepartments
    }));

  };

  return (<>
    {timePeriod && subTarget && (
      <form onSubmit={handleSubmit(onSubmit)}>
        <HStack>
          <Heading as="h2" size="lg" mt={5}>
            Sotto-obiettivo {subTarget?.index}
          </Heading>
          <Spacer />
          <Button
            aria-label={showOnlyEditable ? "Mostra tutti gli indicatori" : "Mostra solo gli indicatori modificabili"}
            rightIcon={
              showOnlyEditable ? <FaListUl /> : <TbFilterEdit />
            } colorScheme='blue' variant='solid' size={"xs"} onClick={() => setShowOnlyEditable(!showOnlyEditable)}>
            {
              showOnlyEditable ? "Mostra tutti gli indicatori" : "Mostra solo indicatori modificabili"
            }
          </Button>
        </HStack>

        <Text align={"left"} mb={3}> {subTarget?.title}.</Text>
        <Stack divider={<><Divider mb={1} /><Divider /></>} spacing="4">
          {subTarget?.actions.map((action) => {
            return (
              <>
                <Heading as="h3" size="md" mt={5}>
                  Azione {action.index}
                </Heading>
                <Text align={"left"} mb={3}> {action?.title}.</Text>

                {action.indicators.map((indicator, index) => {
                  const isAllowed = checkIfIndicatorIsInRolesArray(documentId, subTarget?._id, indicator?._id, roles, true, isUserAdmin)
                  const isAllowedAttachments = checkIfIndicatorIsInRolesArray(documentId, subTarget?._id, indicator?._id, roles, false, isUserAdmin)
                  {
                    counter++
                  }
                  return (
                    <>
                      {
                        showOnlyEditable && !isAllowed ? <></> :
                          <Card mb={5} variant={"filled"} size={"sm"}>
                            <CardHeader>
                              <Heading as="h4" textTransform="uppercase" size={"xs"}>
                                Indicatore {indicator?.index} - {indicator?.title}
                              </Heading>

                              <Flex flexDirection={"row"} my={1}>
                                {
                                  isAllowed == false &&
                                  <>
                                    <Tag colorScheme='red'>Indicatore non modificabile 🔒</Tag>
                                    <Box mx={1}>
                                      <Divider orientation='vertical' />
                                    </Box>
                                  </>
                                }
                                {
                                  <>
                                    {
                                      attributesSdg?.map((item: any) => {
                                        return indicator?.sdg?.includes(item.code) &&
                                          <Popover placement={"top"}>
                                            <PopoverTrigger>
                                              <Tag size="sm" key={item.code} bg={""} className={`sdg-${item.index}`} variant="solid" mx={1}>
                                                SDG #{item.index}
                                              </Tag>
                                            </PopoverTrigger>
                                            <PopoverContent maxW={"128px"}>
                                              <PopoverHeader fontSize={"small"}>
                                                <Center>
                                                  {item.abbr}
                                                </Center>
                                              </PopoverHeader>
                                              <PopoverBody>
                                                <Center>
                                                  <Image src={require(`assets/img/sdg-icons/${item.icon}`)} alt={item.abbr} title={item.abbr} boxSize="64px" display={"inline"} mx={1} />
                                                </Center>
                                              </PopoverBody>
                                            </PopoverContent>
                                          </Popover>
                                      })
                                    }
                                    {
                                      ((indicator?.pnrr?.length > 0) && (indicator?.sdg?.length > 0)) &&
                                      <Box mx={1}>
                                        <Divider orientation='vertical' />
                                      </Box>
                                    }
                                    {
                                      attributesPnrr?.map((item: any) => {
                                        return indicator?.pnrr?.includes(item.code) &&

                                          <Popover placement={"top"}>
                                            <PopoverTrigger>
                                              <Tag size="sm" key={item.code} bg={""} className={`pnrr-${item.index}`} variant="solid" color={"black"} mx={1}>
                                                PNRR #{item.index}
                                              </Tag>
                                            </PopoverTrigger>
                                            <PopoverContent maxW={"128px"}>
                                              <PopoverHeader fontSize={"small"}>
                                                <Center>
                                                  {item.title}
                                                </Center>
                                              </PopoverHeader>
                                              <PopoverBody>
                                                <Center>
                                                  <Image src={require(`assets/img/pnrr-icons/${item.icon}`)} alt={item.title} title={item.title} boxSize="48px" display={"inline"} mx={1} />
                                                </Center>
                                              </PopoverBody>
                                            </PopoverContent>
                                          </Popover>
                                      })
                                    }
                                    {
                                      ((indicator?.anvur?.length > 0) && ((indicator?.pnrr?.length > 0) || (indicator?.sdg?.length > 0))) &&
                                      <Box mx={1}>
                                        <Divider orientation='vertical' />
                                      </Box>
                                    }
                                    {
                                      attributesAnvur?.map((item: any) => {
                                        return indicator?.anvur?.includes(item.code) &&
                                          <Popover placement={"top"}>
                                            <PopoverTrigger>
                                              <Tag size="sm" key={item.code} bg={""} className={`anvur-tags`} variant="solid" mx={1}>
                                                ANVUR - {item.abbr}
                                              </Tag>
                                            </PopoverTrigger>
                                            <PopoverContent maxW={"128px"}>
                                              <PopoverHeader fontSize={"small"}>
                                                <Center>
                                                  {item.title}
                                                </Center>
                                              </PopoverHeader>
                                              <PopoverBody>
                                                <Center>
                                                  <Image src={require(`assets/img/anvur-icons/${item.icon}`)} alt={item.title} title={item.title} boxSize="48px" display={"inline"} mx={1} />
                                                </Center>
                                              </PopoverBody>
                                            </PopoverContent>
                                          </Popover>
                                      })
                                    }
                                    {
                                      (indicator?.anvur?.length > 0) &&
                                      <Box mx={1}>
                                        <Divider orientation='vertical' />
                                      </Box>
                                    }
                                    {
                                      indicator.strategicPlan && (
                                        <Tag colorScheme='blue' size="sm" >Piano strategico</Tag>
                                      )
                                    }
                                  </>
                                }

                              </Flex>
                            </CardHeader>

                            <CardBody>
                              <Box className="indicator-card-body-inputs" p={1}>
                                <Flex flexDir={"row-reverse"} mb={2}>
                                  <Link to={`/targets/${target.index}/subtargets/${subTarget.index}/actions/${action.index}/indicators/${indicator.index}/${timePeriod}/${isReadOnly ? "view" : "edit"}`}
                                    aria-label="Visualizza lista indicatori"
                                    state={{
                                      documentId: documentId,
                                      targetId: targetId,
                                      subTargetId: subTarget?._id,
                                      actionId: action?._id,
                                      indicatorId: indicator?._id,
                                      timePeriod: timePeriod,
                                      isReadOnly: isReadOnly
                                    }}
                                  >
                                    <IconButton aria-label="Visualizza indicatori listati" icon={<FaList />} colorScheme="gray" variant="solid" size={"xs"} />
                                  </Link>
                                  <Link to="">
                                    <IconButton mr={1} aria-label="Visualizza indicatori tabulati" icon={<FaTable />}
                                      variant="solid"
                                      size={"xs"}
                                      isActive={isGridView}
                                      onClick={() => setisGridView(!isGridView)}
                                      title={"Visualizza lista campi in forma tabellare"}
                                    />
                                  </Link>
                                </Flex>

                                {!isGridView ? (

                                  <HStack className="indicator-card-body-inputs">
                                    <IconButton aria-label="Scorri verso sinistra" icon={<FaChevronLeft />}
                                      backgroundColor={"gray.300"}
                                      onClick={() => scrollLeft(indicator.index)}
                                      minH={"10vh"}
                                      margin={0}
                                    />

                                    <HStack overflow={"scroll"} overflowX={"scroll"} ref={elementRefs[indicator.index]} height={"100%"}>
                                      {
                                        divisions.map((department: any) => {
                                          return (
                                            <FormControl key={`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department?._id}`} minW={"6em"} height={"100%"}>
                                              <FormLabel htmlFor={`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department?._id}`} mb="0" fontSize={"xs"}>
                                                <abbr title={department.title}>
                                                  {department.abbr}
                                                </abbr>
                                              </FormLabel>
                                              <Input
                                                id={`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department?._id}`}
                                                placeholder={indicator?.placeholder}
                                                size="lg"
                                                bgColor={"white"}
                                                readOnly={isReadOnly || (isAllowed == false)}
                                                pattern="(\d*|np)"
                                                title="Inserisci un numero o la stringa 'NP'"
                                                onKeyUp={() => updateTotals(`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`)}
                                                {
                                                ...register(`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department?._id}`)
                                                }
                                              />
                                            </FormControl>
                                          )
                                        })
                                      }
                                    </HStack>

                                    <IconButton aria-label="Scorri verso destra" icon={<FaChevronRight />}
                                      backgroundColor={"gray.300"}
                                      onClick={() => scrollRight(indicator.index)}
                                      minH={"10vh"}
                                      margin={0}
                                    />
                                  </HStack>
                                ) : (
                                  <>
                                    <Grid templateColumns="repeat(5, 1fr)" gap={4} >
                                      {
                                        divisions.map((department: any) => {
                                          return (
                                            <GridItem >
                                              <FormControl key={`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department?._id}`} minW={"6em"} height={"100%"}>
                                                <FormLabel htmlFor={`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department?._id}`} mb="0" fontSize={"xs"}>
                                                  <abbr title={department.title}>
                                                    {department.abbr}
                                                  </abbr>
                                                </FormLabel>
                                                <Input
                                                  id={`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department?._id}`}
                                                  placeholder={indicator?.placeholder}
                                                  size="lg"
                                                  bgColor={"white"}
                                                  pattern="(\d*|np)"
                                                  title="Inserisci un numero o la stringa 'NP'"
                                                  readOnly={isReadOnly || (isAllowed == false)}
                                                  onKeyUp={() => updateTotals(`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`)}
                                                  {
                                                  ...register(`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}-${department._id}`)
                                                  }
                                                />
                                              </FormControl>
                                            </GridItem>
                                          )
                                        })
                                      }
                                    </Grid>
                                  </>
                                )
                                }
                                <Flex flexDir={"row-reverse"} py={1}>
                                  <Tag colorScheme='gray'>
                                    Totale AC+CAB+CAM:
                                    {

                                      totalsCentralAdministration && totalsCentralAdministration[`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`] ? totalsCentralAdministration[`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`] : 0

                                    }
                                  </Tag>
                                  <Tag colorScheme='gray' mr={1}>
                                    Totale dipartimenti:
                                    {

                                      totalsDepartments && totalsDepartments[`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`] ? totalsDepartments[`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`] : 0

                                    }
                                  </Tag>
                                </Flex>

                                {!isReadOnly && isAllowed &&
                                  <Flex flexDir={"row-reverse"} mt={2}>
                                    <Button aria-label="Salva dati" size={"xs"} colorScheme="green" onClick={() => saveData(`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`)}>
                                      Salva modifiche indicatore {indicator?.index}
                                    </Button>
                                  </Flex>
                                }

                              </Box>

                            </CardBody>

                            <CardFooter minW={"100%"} display={"block"}>
                              <Box >
                                <SourcesList sources={getNamesFromCodes(indicator.sources, sources)} organizationalUnit={getNamesFromCodes(indicator.organizationalUnit, organizationalUnits)} />
                              </Box>

                              <Box >
                                <AttachmentsList documentId={documentId} index={indicator.index} path={`indicator-${targetId}-${subTarget?._id}-${action?._id}-${indicator?._id}`} isReadOnly={isReadOnly} checkPermission={isAllowedAttachments} attachments={indicator.attachments ?? []} sources={indicator.sources} />
                              </Box>
                            </CardFooter>
                          </Card >
                      }

                    </>
                  );
                }

                )}
              </>
            );
          })}
          {!isReadOnly && (
            <Button variant={"solid"} colorScheme="green" type="submit">
              Salva modifiche
            </Button>
          )}
        </Stack >
      </form >

    )
    }</>);
}